import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Bold, Italic, Link2, List, ListOrdered, Minus, SquareMousePointer } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Button } from './button.js';
/**
 * Wrap the current textarea selection with `before` / `after` tags. If nothing
 * is selected, inserts the tags with the caret placed between them. Preserves
 * undo history by using the native `setRangeText` API.
 */
function wrapSelection(el, before, after, onChange) {
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    const selected = el.value.slice(start, end);
    const next = el.value.slice(0, start) + before + selected + after + el.value.slice(end);
    onChange(next);
    // Schedule caret adjustment after React re-renders the new value.
    queueMicrotask(() => {
        el.focus();
        const caret = start + before.length + selected.length;
        el.setSelectionRange(caret, caret);
    });
}
/**
 * Escape `& < > "` so operator-entered text/URLs can't break the authored
 * markup (defense-in-depth — the HTML is sanitized at save). `&` first so the
 * entities introduced below aren't double-escaped.
 */
function esc(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function insertAtCursor(el, text, onChange) {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const next = el.value.slice(0, start) + text + el.value.slice(end);
    onChange(next);
    queueMicrotask(() => {
        el.focus();
        const caret = start + text.length;
        el.setSelectionRange(caret, caret);
    });
}
function ToolbarButton({ onClick, disabled, label, children }) {
    return (_jsx(Button, { type: "button", variant: "ghost", size: "icon", onMouseDown: (e) => e.preventDefault(), onClick: onClick, disabled: disabled, "aria-label": label, title: label, children: children }));
}
/**
 * Source-mode HTML editor. We deliberately avoid a WYSIWYG (Tiptap et al.)
 * because email-safe HTML is inline-styles-only: Gmail, Apple Mail, and most
 * other clients strip `<style>` blocks and respect only attributes like
 * `style="..."`. WYSIWYG editors cannot round-trip those inline styles
 * through their own schemas — styling like `style="max-width:600px"` on a
 * `<div>` gets dropped on every save. A plain monospace textarea is lossless.
 *
 * The toolbar provides one-click insertion of common email-safe snippets
 * (wrapping the selection in `<strong>`, `<em>`, `<a>`, `<ul>/<li>`, `<hr>`)
 * so agents don't have to hand-type tags for routine edits, but the raw HTML
 * is always visible and editable.
 */
export function HtmlEditor({ value, onChange, placeholder, hideToolbar = false, maxHeight = '320px', autoFocus = false, id, className, ...rest }) {
    const testId = rest['data-testid'];
    const ariaLabel = rest['aria-label'];
    const ariaInvalid = rest['aria-invalid'];
    const ariaDescribedBy = rest['aria-describedby'];
    const ref = React.useRef(null);
    const withTextarea = (fn) => () => {
        const el = ref.current;
        if (el)
            fn(el);
    };
    return (_jsxs("div", { "data-testid": testId, className: cn('flex flex-col overflow-hidden rounded-[var(--radius-2)] border border-[var(--color-border-default)] bg-[var(--color-surface-elevated)]', 'focus-within:ring-2 focus-within:ring-[var(--color-border-focus)]', className), children: [!hideToolbar ? (_jsxs("div", { className: "flex flex-wrap gap-1 rounded-t-[var(--radius-2)] border-b border-[var(--color-border-default)] bg-[var(--color-surface-canvas)] px-2 py-1.5", children: [_jsx(ToolbarButton, { label: "Bold", onClick: withTextarea((el) => wrapSelection(el, '<strong>', '</strong>', onChange)), children: _jsx(Bold, { className: "h-4 w-4", "aria-hidden": true }) }), _jsx(ToolbarButton, { label: "Italic", onClick: withTextarea((el) => wrapSelection(el, '<em>', '</em>', onChange)), children: _jsx(Italic, { className: "h-4 w-4", "aria-hidden": true }) }), _jsx(ToolbarButton, { label: "Link", onClick: withTextarea((el) => {
                            const href = typeof window !== 'undefined' ? window.prompt('Link URL', 'https://') : null;
                            if (!href)
                                return;
                            wrapSelection(el, `<a href="${esc(href)}" style="color:#1155cc;text-decoration:underline;">`, '</a>', onChange);
                        }), children: _jsx(Link2, { className: "h-4 w-4", "aria-hidden": true }) }), _jsx(ToolbarButton, { label: "\u041A\u043D\u043E\u043F\u043A\u0430", onClick: withTextarea((el) => {
                            if (typeof window === 'undefined')
                                return;
                            const text = window.prompt('Текст кнопки', 'Войти в аккаунт');
                            if (!text)
                                return;
                            const href = window.prompt('Ссылка (URL или переменная {{data.провайдер.поле}})', 'https://');
                            if (!href)
                                return;
                            const button = `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:16px 0;">` +
                                `<tr><td style="border-radius:6px;background:#5b5bd6;">` +
                                `<a href="${esc(href)}" style="display:inline-block;padding:12px 24px;font-family:Arial,Helvetica,sans-serif;` +
                                `font-size:14px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:6px;">${esc(text)}</a>` +
                                `</td></tr></table>`;
                            insertAtCursor(el, button, onChange);
                        }), children: _jsx(SquareMousePointer, { className: "h-4 w-4", "aria-hidden": true }) }), _jsx(ToolbarButton, { label: "Bullet list", onClick: withTextarea((el) => insertAtCursor(el, '\n<ul style="margin:0 0 16px 20px;padding:0;">\n  <li style="margin:0 0 8px 0;"></li>\n</ul>\n', onChange)), children: _jsx(List, { className: "h-4 w-4", "aria-hidden": true }) }), _jsx(ToolbarButton, { label: "Numbered list", onClick: withTextarea((el) => insertAtCursor(el, '\n<ol style="margin:0 0 16px 20px;padding:0;">\n  <li style="margin:0 0 8px 0;"></li>\n</ol>\n', onChange)), children: _jsx(ListOrdered, { className: "h-4 w-4", "aria-hidden": true }) }), _jsx(ToolbarButton, { label: "Horizontal rule", onClick: withTextarea((el) => insertAtCursor(el, '\n<hr style="border:none;border-top:1px solid #e6e6e6;margin:16px 0;" />\n', onChange)), children: _jsx(Minus, { className: "h-4 w-4", "aria-hidden": true }) })] })) : null, _jsx("textarea", { ref: ref, id: id, value: value, onChange: (e) => onChange(e.target.value), placeholder: placeholder, autoFocus: autoFocus, spellCheck: false, "aria-label": ariaLabel, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, className: cn('block w-full resize-y overflow-auto px-3 py-2 font-mono text-xs leading-relaxed', 'bg-transparent text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)]'), style: { minHeight: '10rem', maxHeight } })] }));
}
//# sourceMappingURL=html-editor.js.map